import React, { useState, useEffect } from 'react';
import BackButtonBottom from '../../components/common/BackButtonBottom';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';

// ═══════════════════════════════════════════════════════════════
// MY DOCUMENTS —” Driver page (Batch 16 M1)
// ═══════════════════════════════════════════════════════════════
// View and update driver compliance documents.
// Expiry status colour-coded.
// ═══════════════════════════════════════════════════════════════

const DOCUMENT_TYPES = [
  { key: 'driverLicence', label: 'NSW Driver Licence (incl. PT Code)', required: true },
  { key: 'vehicleRegistration', label: 'Vehicle Registration', required: true },
  { key: 'ctpInsurance', label: 'CTP Green Slip', required: true },
  { key: 'comprehensiveInsurance', label: 'Comprehensive Insurance', required: true },
  { key: 'wwcc', label: 'Working With Children Check', required: false },
];

export default function MyDocuments() {
const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadDriver = async () => {
if (!currentUser) return;
    const snap = await getDoc(doc(db, 'users', currentUser.uid));
    if (snap.exists()) setDriver({ id: snap.id, ...snap.data() });
  };

useEffect(() => { loadDriver(); }, [currentUser?.uid]);

  const daysUntil = (date) => {
    if (!date) return null;
    const d = date.toDate ? date.toDate() : new Date(date);
    return Math.floor((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  };

  const statusOf = (docData) => {
    if (!docData || !docData.expiryDate) return { label: 'Not on file', color: '#94a3b8', bg: '#f1f5f9' };
    const days = daysUntil(docData.expiryDate);
    if (days < 0) return { label: `Expired ${Math.abs(days)}d ago`, color: '#dc2626', bg: '#fee2e2' };
    if (days <= 7) return { label: `Expires in ${days}d`, color: '#dc2626', bg: '#fee2e2' };
    if (days <= 30) return { label: `Expires in ${days}d`, color: '#92400e', bg: '#fef3c7' };
    return { label: `Valid for ${days}d`, color: '#059669', bg: 'rgba(45,212,168,0.1)' };
  };

  const startEdit = (docType) => {
    const existing = driver?.documents?.[docType] || {};
    setEditingType(docType);
    setEditValues({
      expiryDate: existing.expiryDate
        ? (existing.expiryDate.toDate ? existing.expiryDate.toDate() : new Date(existing.expiryDate))
            .toISOString().split('T')[0]
        : '',
      documentNumber: existing.documentNumber || '',
    });
    setError('');
  };

  const handleSave = async () => {
    if (!editValues.expiryDate) { setError('Expiry date required'); return; }
    setSaving(true);
    setError('');
    try {
      const callable = httpsCallable(functions, 'updateDriverDocument');
      await callable({
        docType: editingType,
        expiryDate: editValues.expiryDate,
        documentNumber: editValues.documentNumber,
      });
      await loadDriver();
      setEditingType(null);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!driver) {
    return <div className="p-10 text-center">Loading...</div>;
  }

  return (
    <div className="max-w-[700px] mx-auto p-6">
      <button onClick={() => navigate(-1)} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0 mb-4">← Back</button>

      <h1 className="font-serif mt-0 mb-2">My Documents</h1>
      <p className="text-body text-[0.9rem] mb-4">
        Keep your compliance documents up to date to continue accepting bookings.
        You'll receive reminders 30, 7, and 1 days before each expires.
      </p>

      {driver.documentsBlocked && (
        <div className="p-3.5 rounded-xl mb-5" style={{ background: '#fee2e2', border: '1px solid #fecaca', color: '#7f1d1d' }}>
          <strong>Account blocked.</strong> One or more of your documents has expired.
          Update the expired document(s) below to resume accepting bookings.
        </div>
      )}

      <div className="grid gap-2.5">
        {DOCUMENT_TYPES.map(({ key, label, required }) => {
          const docData = driver.documents?.[key];
          const status = statusOf(docData);
          const isEditing = editingType === key;

          return (
            <div key={key} className="p-3.5 rounded-xl bg-white border border-slate-200">
              {!isEditing ? (
                <>
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1">
                      <div className="font-bold text-brand-dark">
                        {label}
                        {!required && <span className="text-[0.7rem] text-muted ml-1.5 font-normal">(optional)</span>}
                      </div>
                      {docData?.documentNumber && (
                        <div className="text-[0.78rem] text-muted mt-0.5">
                          #{docData.documentNumber}
                        </div>
                      )}
                      {docData?.verifiedByAdmin === false && docData?.expiryDate && (
                        <div className="text-[0.75rem] mt-1" style={{ color: '#92400e' }}>
                          â³ Pending admin verification
                        </div>
                      )}
                    </div>
                    <span className="px-2.5 py-1 rounded-full text-[0.73rem] font-bold whitespace-nowrap" style={{ background: status.bg, color: status.color }}>
                      {status.label}
                    </span>
                  </div>
                  <button onClick={() => startEdit(key)} className="mt-2.5 px-3 py-1.5 rounded-lg border-[1.5px] border-brand-primary bg-white text-brand-primary cursor-pointer font-semibold text-[0.82rem]">
                    {docData ? 'Update' : 'Add document'}
                  </button>
                </>
              ) : (
                <>
                  <div className="font-bold mb-3">{label}</div>
                  <label className="text-[0.82rem] text-body block mb-1">
                    Document number / reference
                  </label>
                  <input
                    type="text"
                    value={editValues.documentNumber}
                    onChange={(e) => setEditValues({ ...editValues, documentNumber: e.target.value })}
                    className="w-full p-2.5 rounded-lg border border-slate-200 mb-2.5 text-[0.9rem]"
                  />
                  <label className="text-[0.82rem] text-body block mb-1">
                    Expiry date
                  </label>
                  <input
                    type="date"
                    value={editValues.expiryDate}
                    onChange={(e) => setEditValues({ ...editValues, expiryDate: e.target.value })}
                    className="w-full p-2.5 rounded-lg border border-slate-200 mb-2.5 text-[0.9rem]"
                  />
                  {error && <div className="text-[0.82rem] mb-2" style={{ color: '#dc2626' }}>{error}</div>}
                  <div className="flex gap-2">
                    <button onClick={handleSave} disabled={saving} className="flex-1 px-3 py-2 rounded-lg border-0 bg-brand-primary text-white cursor-pointer font-bold text-[0.88rem]">
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => { setEditingType(null); setError(''); }} className="flex-1 px-3 py-2 rounded-lg border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold text-[0.88rem]">
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 p-3.5 rounded-xl bg-slate-50 border border-slate-200 text-[0.82rem] text-body leading-relaxed">
        <strong>After updating</strong>, your document will be marked "Pending admin verification".
        Reservo Admin will review and verify within 24 hours. If your account was blocked, it will be
        unblocked automatically once all documents are current.
      </div>
      <BackButtonBottom label="Back" />
    </div>
  );
}